import {
  getApplicablePromotionRoom,
  calculatePromotion,
  arrangePromos,
} from "./usePriceCalculator";
import { getCalculatedTax } from "./utils";

// Configuration for package generation
const PACKAGE_CONFIG = {
  AUTO_SELECT: true,
  SHOW_SAVINGS: true,
  MAX_PACKAGES: 3,
  ENABLE_MULTI_ROOM_TYPES: true, // Enable mixing different room types
  MAX_ROOM_TYPE_MIX: 3, // Maximum different room types in one package
  CATEGORIES: [
    "budget",
    //  'smart-mix',
    "convenience",
    "luxury",
  ],
  MINIMUM_ADULTS_FOR_PACKAGES:
    process.env.REACT_APP_MIN_ADULTS_FOR_PACKAGES || 5, // Only generate packages when adults >= this value
  ENABLE_PACKAGE_LOGGING:
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_DEBUG_PACKAGES === "true", // Toggle detailed console logs
};

// Optimally distribute guests across room types (cheapest first, maximum utilization)
function distributeGuestsOptimally(
  sortedRooms,
  totalAdults,
  totalChildren,
  totalInfants,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  const packageRooms = [];
  let remainingAdults = totalAdults;
  let remainingChildren = totalChildren;
  let remainingInfants = totalInfants;

  if (PACKAGE_CONFIG.ENABLE_PACKAGE_LOGGING) {
  }

  // Check if we have any valid rooms
  const validRooms = sortedRooms.filter(
    (room) =>
      room &&
      room.availableRooms > 0 &&
      room.ratePlans &&
      Object.keys(room.ratePlans).length > 0
  );

  if (validRooms.length === 0) {
    return null;
  }

  // Log all available rooms with their prices

  validRooms.forEach((room, idx) => {
    const roomName =
      room.name || room.description?.substring(0, 30) || `Room ${room.id}`;
  });

  // Process each room type in order (cheapest first)
  for (let roomIndex = 0; roomIndex < sortedRooms.length; roomIndex++) {
    const room = sortedRooms[roomIndex];

    // Check if all guests are accommodated
    if (
      remainingAdults <= 0 &&
      remainingChildren <= 0 &&
      remainingInfants <= 0
    ) {
      break;
    }

    // Skip rooms with no availability
    const roomAvailability = room.availableRooms || 0;
    if (roomAvailability === 0) {
      const roomName =
        room.name || room.description?.substring(0, 30) || `Room ${room.id}`;

      continue;
    }

    if (!room.ratePlans || Object.keys(room.ratePlans).length === 0) {
      const roomName =
        room.name || room.description?.substring(0, 30) || `Room ${room.id}`;

      continue;
    }

    const roomName =
      room.name || room.description?.substring(0, 30) || `Room ${room.id}`;

    // Get room constraints
    const roomMaxAdults = room.maxAdultCount || room.maxGuestCount || 4;
    const roomMaxChildren = room.maxChildCount || 2;
    const roomMaxInfants = room.maxInfantCount || 1;
    const roomMaxGuests = room.maxGuestCount || 4;

    // Find cheapest rate plan for this room type
    let bestRatePlan = null;
    let bestRatePlanCode = null;
    let bestPrice = Infinity;

    for (const [code, ratePlan] of Object.entries(room.ratePlans)) {
      if (!ratePlan) continue;

      // Add code to ratePlan object - use ratePlanCode if it exists, otherwise use the key
      const actualCode = ratePlan.ratePlanCode || code;
      const ratePlanWithCode = {
        ...ratePlan,
        code: actualCode,
        ratePlanCode: actualCode,
      };

      // Calculate a sample price for comparison (use max occupancy for fair comparison)
      const testAdults = Math.min(roomMaxAdults, 2); // Use 2 adults as standard for comparison
      const testChildren = 0;
      const testInfants = 0;

      const samplePrice = calculateRoomPrice(
        room,
        testAdults,
        testChildren,
        testInfants,
        ratePlanWithCode,
        promotions,
        taxes,
        hotelData,
        dateCount
      );

      if (samplePrice && samplePrice.totalPrice < bestPrice) {
        bestRatePlan = ratePlanWithCode;
        bestRatePlanCode = code;
        bestPrice = samplePrice.totalPrice;
      }
    }

    if (!bestRatePlan) {
      continue;
    }

    // Calculate how many rooms we need and can use from this type
    const minRoomsNeeded = Math.ceil(remainingAdults / roomMaxAdults);
    const maxRoomsToUse = Math.min(roomAvailability, minRoomsNeeded + 2); // Allow some flexibility

    // Now distribute guests across available rooms of this type
    let roomsUsed = 0;
    const roomsAddedForThisType = [];

    // Use ALL available rooms of this type before moving to next type
    while (roomsUsed < roomAvailability && remainingAdults > 0) {
      // Calculate optimal guest distribution for this room
      let adultsInThisRoom = Math.min(remainingAdults, roomMaxAdults);

      // If this is the last room we can use and we still have remaining adults,
      // try to fit as many as possible
      if (
        roomsUsed === roomAvailability - 1 &&
        remainingAdults > roomMaxAdults
      ) {
        // Check if there are more room types available after this
        const hasMoreRoomTypes =
          roomIndex < sortedRooms.length - 1 &&
          sortedRooms.slice(roomIndex + 1).some((r) => r.availableRooms > 0);

        if (!hasMoreRoomTypes) {
          // This is our last chance, try to fit everyone
          adultsInThisRoom = Math.min(remainingAdults, roomMaxAdults);
        }
      }

      // Must have at least 1 adult per room
      if (adultsInThisRoom === 0) {
        break;
      }

      const childrenInThisRoom = Math.min(
        remainingChildren,
        roomMaxChildren,
        Math.max(0, roomMaxGuests - adultsInThisRoom)
      );

      const infantsInThisRoom = Math.min(
        remainingInfants,
        roomMaxInfants,
        Math.max(0, roomMaxGuests - adultsInThisRoom - childrenInThisRoom)
      );

      // Calculate price for this room configuration
      const roomPrice = calculateRoomPrice(
        room,
        adultsInThisRoom,
        childrenInThisRoom,
        infantsInThisRoom,
        bestRatePlan,
        promotions,
        taxes,
        hotelData,
        dateCount
      );

      if (!roomPrice) {
        break;
      }

      // Add this room to the package
      packageRooms.push(roomPrice);
      roomsAddedForThisType.push({
        adults: adultsInThisRoom,
        children: childrenInThisRoom,
        infants: infantsInThisRoom,
        price: roomPrice.totalPrice,
      });

      // Update remaining guests
      remainingAdults -= adultsInThisRoom;
      remainingChildren -= childrenInThisRoom;
      remainingInfants -= infantsInThisRoom;
      roomsUsed++;
    }

    if (roomsUsed > 0) {
    }
  }

  // Final check if all guests were accommodated
  if (remainingAdults > 0 || remainingChildren > 0 || remainingInfants > 0) {
    return null;
  }

  // Success - log summary

  const roomTypeSummary = {};
  packageRooms.forEach((room) => {
    const type = room.roomName || "Unknown";
    if (!roomTypeSummary[type]) {
      roomTypeSummary[type] = { count: 0, totalPrice: 0 };
    }
    roomTypeSummary[type].count++;
    roomTypeSummary[type].totalPrice += room.totalPrice;
  });

  Object.entries(roomTypeSummary).forEach(([type, info]) => {});

  const totalPackagePrice = packageRooms.reduce(
    (sum, room) => sum + room.totalPrice,
    0
  );

  return packageRooms;
}

// Generate all possible distributions of guests across rooms
function generateAllDistributions(
  totalAdults,
  totalChildren,
  totalInfants,
  maxRooms,
  maxAdults,
  maxChildren,
  maxInfants,
  maxGuests
) {
  const distributions = [];

  // Check if children/infants are allowed
  if (
    (totalChildren > 0 && !maxChildren) ||
    (totalInfants > 0 && !maxInfants)
  ) {
    return [];
  }

  // Calculate minimum rooms needed
  const minRoomsNeeded = Math.max(
    Math.ceil(totalAdults / maxAdults),
    Math.ceil((totalAdults + totalChildren + totalInfants) / maxGuests)
  );

  if (minRoomsNeeded > maxRooms) {
    return [];
  }

  // Recursive function to generate distributions
  function generateDistributionsRecursive(
    remainingAdults,
    remainingChildren,
    remainingInfants,
    currentDistribution,
    roomsUsed
  ) {
    // Base case: all guests distributed
    if (
      remainingAdults === 0 &&
      remainingChildren === 0 &&
      remainingInfants === 0
    ) {
      distributions.push([...currentDistribution]);
      return;
    }

    // Base case: no more rooms available
    if (roomsUsed >= maxRooms) {
      return;
    }

    // Base case: impossible to fit remaining guests
    if (remainingAdults > (maxRooms - roomsUsed) * maxAdults) {
      return;
    }

    // Try different adult counts for this room (at least 1 adult per room)
    const maxAdultsThisRoom = Math.min(remainingAdults, maxAdults, maxGuests);
    for (let adults = 1; adults <= maxAdultsThisRoom; adults++) {
      const spaceLeft = Math.min(maxGuests - adults, maxGuests);

      // Try different child counts
      const maxChildrenThisRoom = Math.min(
        remainingChildren,
        maxChildren || 0,
        spaceLeft
      );
      for (let children = 0; children <= maxChildrenThisRoom; children++) {
        const spaceAfterChildren = spaceLeft - children;

        // Try different infant counts
        const maxInfantsThisRoom = Math.min(
          remainingInfants,
          maxInfants || 0,
          spaceAfterChildren
        );
        for (let infants = 0; infants <= maxInfantsThisRoom; infants++) {
          const roomOccupancy = {
            adults,
            children,
            infants,
          };

          currentDistribution.push(roomOccupancy);
          generateDistributionsRecursive(
            remainingAdults - adults,
            remainingChildren - children,
            remainingInfants - infants,
            currentDistribution,
            roomsUsed + 1
          );
          currentDistribution.pop();
        }
      }
    }
  }

  generateDistributionsRecursive(
    totalAdults,
    totalChildren,
    totalInfants,
    [],
    0
  );

  // Remove duplicate distributions and sort by number of rooms used
  const uniqueDistributions = [];
  const seen = new Set();

  for (const dist of distributions) {
    // Create a sorted string representation for comparison
    const sortedDist = dist
      .map((r) => `${r.adults}-${r.children}-${r.infants}`)
      .sort()
      .join("|");

    if (!seen.has(sortedDist)) {
      seen.add(sortedDist);
      uniqueDistributions.push(dist);
    }
  }

  return uniqueDistributions;
}

// Calculate total price for a distribution
function calculateDistributionTotalPrice(
  distribution,
  room,
  ratePlan,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  let totalPrice = 0;
  let allRoomDetails = [];

  for (let i = 0; i < distribution.length; i++) {
    const occupancy = distribution[i];

    const roomPrice = calculateRoomPrice(
      room,
      occupancy.adults,
      occupancy.children,
      occupancy.infants,
      ratePlan,
      promotions,
      taxes,
      hotelData,
      dateCount
    );

    if (!roomPrice) {
      return null; // Invalid configuration
    }

    totalPrice += roomPrice.totalPrice;
    allRoomDetails.push(roomPrice);
  }

  return {
    totalPrice,
    roomDetails: allRoomDetails,
    distribution,
  };
}

// Find optimal distribution for a room
function findOptimalDistributionForRoom(
  room,
  ratePlan,
  remainingAdults,
  remainingChildren,
  remainingInfants,
  optimizationType,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  // Get room constraints with proper fallbacks
  const roomMaxAdults = room.maxAdultCount || room.maxGuestCount || 4;
  const roomMaxChildren = room.maxChildCount || 2;
  const roomMaxInfants = room.maxInfantCount || 1;
  const roomMaxGuests = room.maxGuestCount || 4;

  // Generate all possible distributions
  const distributions = generateAllDistributions(
    remainingAdults,
    remainingChildren,
    remainingInfants,
    room.availableRooms,
    roomMaxAdults,
    roomMaxChildren,
    roomMaxInfants,
    roomMaxGuests
  );

  if (distributions.length === 0) {
    return null;
  }

  // Calculate prices for all distributions
  const pricedDistributions = [];

  for (const distribution of distributions) {
    const priceInfo = calculateDistributionTotalPrice(
      distribution,
      room,
      ratePlan,
      promotions,
      taxes,
      hotelData,
      dateCount
    );

    if (priceInfo) {
      pricedDistributions.push(priceInfo);
    }
  }

  if (pricedDistributions.length === 0) {
    return null;
  }

  // Sort and select based on optimization type
  let selectedDistribution;

  if (optimizationType === "budget") {
    // Select cheapest distribution
    pricedDistributions.sort((a, b) => a.totalPrice - b.totalPrice);
    selectedDistribution = pricedDistributions[0];
  } else if (optimizationType === "luxury") {
    // Select most expensive distribution
    pricedDistributions.sort((a, b) => b.totalPrice - a.totalPrice);
    selectedDistribution = pricedDistributions[0];
  } else if (optimizationType === "convenience") {
    // Select middle-priced distribution with balanced occupancy
    // First, prefer distributions with similar occupancy across rooms
    const calculateBalance = (dist) => {
      const adultCounts = dist.distribution.map((r) => r.adults);
      const avg = adultCounts.reduce((a, b) => a + b, 0) / adultCounts.length;
      return adultCounts.reduce((sum, count) => sum + Math.abs(count - avg), 0);
    };

    // Sort by balance first, then by price
    pricedDistributions.sort((a, b) => {
      const balanceA = calculateBalance(a);
      const balanceB = calculateBalance(b);
      if (Math.abs(balanceA - balanceB) > 0.5) {
        return balanceA - balanceB;
      }
      return a.totalPrice - b.totalPrice;
    });

    // Select middle option from balanced distributions
    const balancedDistributions = pricedDistributions.slice(
      0,
      Math.ceil(pricedDistributions.length / 2)
    );
    selectedDistribution =
      balancedDistributions[Math.floor(balancedDistributions.length / 2)];
  }

  return selectedDistribution;
}

// Generate all possible room type combinations for multi-room packages
function generateRoomTypeCombinations(
  rooms,
  totalAdults,
  totalChildren,
  totalInfants,
  maxRoomTypes = 3
) {
  const combinations = [];
  const validRooms = rooms.filter(
    (room) =>
      room &&
      room.availableRooms > 0 &&
      room.ratePlans &&
      Object.keys(room.ratePlans).length > 0
  );

  // Check for limited availability scenario
  let hasLimitedAvailability = false;
  validRooms.forEach((room) => {
    const roomName =
      room.name || room.description?.substring(0, 30) || `Room ${room.id}`;

    // Check if this room has limited availability relative to needs
    const roomsNeededOfThisType = Math.ceil(totalAdults / room.maxAdultCount);
    if (room.availableRooms < roomsNeededOfThisType) {
      hasLimitedAvailability = true;
    }
  });

  if (hasLimitedAvailability) {
  }

  if (validRooms.length === 0) {
    return [];
  }

  // Calculate minimum rooms needed
  const maxGuestPerRoom = Math.max(...validRooms.map((r) => r.maxGuestCount));
  const minRoomsNeeded = Math.ceil(totalAdults / maxGuestPerRoom);

  // Force multi-room combinations when we have enough guests
  const forceMultiRoom = minRoomsNeeded >= 2 || totalAdults >= 4;

  // Helper function to generate combinations recursively
  function generateCombinations(
    roomIndex,
    remainingAdults,
    remainingChildren,
    remainingInfants,
    currentCombination,
    roomTypesUsed
  ) {
    // Base case: all guests accommodated
    if (
      remainingAdults <= 0 &&
      remainingChildren <= 0 &&
      remainingInfants <= 0
    ) {
      if (currentCombination.length > 0) {
        // Only add if this is a multi-room combination or single room that fits all
        const totalRooms = currentCombination.reduce(
          (sum, item) => sum + item.count,
          0
        );
        combinations.push([...currentCombination]);
      }
      return;
    }

    // Base case: no more rooms to try or too many room types
    if (roomIndex >= validRooms.length || roomTypesUsed >= maxRoomTypes) {
      return;
    }

    const room = validRooms[roomIndex];

    // Get room capacity constraints with proper fallbacks
    const roomMaxAdults = room.maxAdultCount;
    const roomMaxChildren = room.maxChildCount;
    const roomMaxGuests = room.maxGuestCount;

    // Calculate max rooms we can use of this type
    const maxRoomsNeeded = Math.min(
      room.availableRooms,
      Math.ceil(remainingAdults / roomMaxAdults)
    );

    // Try using different counts of this room type
    // Start from 0 to consider skipping this room type
    for (let count = 0; count <= maxRoomsNeeded; count++) {
      if (count === 0) {
        // Skip this room type - but only if we have other options or can already accommodate all
        if (roomIndex < validRooms.length - 1) {
          generateCombinations(
            roomIndex + 1,
            remainingAdults,
            remainingChildren,
            remainingInfants,
            currentCombination,
            roomTypesUsed
          );
        }
      } else {
        // Check if we can accommodate some guests with this room count
        const maxGuestsInRoom = Math.min(
          count * roomMaxAdults,
          remainingAdults
        );

        if (maxGuestsInRoom > 0) {
          // Add this room type to combination
          const newCombination = [
            ...currentCombination,
            {
              room: room,
              count: count,
              maxAdults: roomMaxAdults,
              maxChildren: roomMaxChildren,
              maxInfants: room.maxInfantCount || 1,
              maxGuests: roomMaxGuests,
            },
          ];

          // Calculate how many guests this allocation would handle
          const adultsToAllocate = Math.min(
            remainingAdults,
            count * roomMaxAdults
          );
          const childrenToAllocate = Math.min(
            remainingChildren,
            count * roomMaxChildren
          );
          const infantsToAllocate = Math.min(
            remainingInfants,
            count * (room.maxInfantCount || 1)
          );

          const newRemainingAdults = Math.max(
            0,
            remainingAdults - adultsToAllocate
          );
          const newRemainingChildren = Math.max(
            0,
            remainingChildren - childrenToAllocate
          );
          const newRemainingInfants = Math.max(
            0,
            remainingInfants - infantsToAllocate
          );

          // Always try to recurse to explore more room types
          // The base case will handle adding the combination when all guests are accommodated
          generateCombinations(
            roomIndex + 1,
            newRemainingAdults,
            newRemainingChildren,
            newRemainingInfants,
            newCombination,
            roomTypesUsed + 1
          );
        }
      }
    }
  }

  generateCombinations(0, totalAdults, totalChildren, totalInfants, [], 0);

  // Always try simple multi-room combinations when we have multiple room types
  // This ensures we get multi-room options even for smaller groups
  // CRITICAL: This handles cases where cheapest room has limited availability
  if (validRooms.length > 1 && (forceMultiRoom || totalAdults >= 3)) {
    // Try combinations of 2 different room types
    for (let i = 0; i < validRooms.length; i++) {
      for (let j = i + 1; j < validRooms.length; j++) {
        const room1 = validRooms[i];
        const room2 = validRooms[j];

        // IMPORTANT: When room1 has limited availability (e.g., only 1 room),
        // we should use ALL available room1 and fill remaining with room2
        const room1Availability = room1.availableRooms;
        const room2Availability = room2.availableRooms;

        // Try different distributions, especially when room1 has limited availability
        const maxRoom1 = Math.min(3, room1Availability); // Use up to 3 or all available
        const maxRoom2 = Math.min(3, room2Availability);

        for (let room1Count = 1; room1Count <= maxRoom1; room1Count++) {
          for (let room2Count = 1; room2Count <= maxRoom2; room2Count++) {
            const combination = [
              {
                room: room1,
                count: room1Count,
                maxAdults: room1.maxAdultCount || room1.maxGuestCount || 4,
                maxChildren: room1.maxChildCount || 2,
                maxInfants: room1.maxInfantCount || 1,
                maxGuests: room1.maxGuestCount || 4,
              },
              {
                room: room2,
                count: room2Count,
                maxAdults: room2.maxAdultCount || room2.maxGuestCount || 4,
                maxChildren: room2.maxChildCount || 2,
                maxInfants: room2.maxInfantCount || 1,
                maxGuests: room2.maxGuestCount || 4,
              },
            ];

            // Check if this combination can accommodate all guests
            const totalCapacity =
              room1Count * (room1.maxGuestCount || 4) +
              room2Count * (room2.maxGuestCount || 4);
            const totalAdultCapacity =
              room1Count * (room1.maxAdultCount || room1.maxGuestCount || 4) +
              room2Count * (room2.maxAdultCount || room2.maxGuestCount || 4);

            if (
              totalCapacity >= totalAdults + totalChildren &&
              totalAdultCapacity >= totalAdults
            ) {
              combinations.push(combination);
              const room1Name =
                room1.name ||
                room1.description?.substring(0, 30) ||
                `Room ${room1.id}`;
              const room2Name =
                room2.name ||
                room2.description?.substring(0, 30) ||
                `Room ${room2.id}`;

              // Special handling for limited availability scenario
              if (
                room1Availability === 1 &&
                room1Count === 1 &&
                room2Count >= 1
              ) {
              }
            }
          }
        }
      }
    }
  }

  // Log the types of combinations generated
  const singleTypeCombos = combinations.filter((c) => c.length === 1);
  const multiTypeCombos = combinations.filter((c) => c.length > 1);

  if (multiTypeCombos.length > 0) {
    multiTypeCombos.slice(0, 3).forEach((combo) => {
      const desc = combo
        .map((c) => {
          const roomName =
            c.room.name ||
            c.room.description?.substring(0, 20) ||
            `Room ${c.room.id}`;
          return `${c.count}x${roomName}`;
        })
        .join(" + ");
    });
  }

  // Filter combinations to ensure they can actually accommodate all guests
  const filteredCombinations = combinations.filter((combination) => {
    const totalMaxAdults = combination.reduce(
      (sum, item) => sum + item.count * item.maxAdults,
      0
    );
    const totalMaxChildren = combination.reduce(
      (sum, item) => sum + item.count * item.maxChildren,
      0
    );
    const totalMaxInfants = combination.reduce(
      (sum, item) => sum + item.count * item.maxInfants,
      0
    );

    const canAccommodate =
      totalMaxAdults >= totalAdults &&
      (totalChildren === 0 || totalMaxChildren >= totalChildren) &&
      (totalInfants === 0 || totalMaxInfants >= totalInfants);

    if (!canAccommodate) {
      const comboString = combination
        .map((c) => {
          const roomName =
            c.room.name ||
            c.room.description?.substring(0, 20) ||
            `Room ${c.room.id}`;
          return `${c.count}x${roomName}`;
        })
        .join(" + ");
    } else {
      const isMultiRoom = combination.length > 1;
      const totalRooms = combination.reduce((sum, item) => sum + item.count, 0);
    }

    return canAccommodate;
  });

  // Log multi-room combinations specifically
  const multiRoomCombos = filteredCombinations.filter((c) => c.length > 1);

  // Prioritize true multi-room type combinations (different room types) over single room type
  const sortedCombinations = filteredCombinations.sort((a, b) => {
    // Check if combination uses different room types
    const aHasMultiTypes = a.length > 1;
    const bHasMultiTypes = b.length > 1;

    // Prioritize multi-room type combinations
    if (aHasMultiTypes && !bHasMultiTypes) return -1;
    if (!aHasMultiTypes && bHasMultiTypes) return 1;

    // Among multi-room combinations, prefer 2-3 room types over too many
    if (aHasMultiTypes && bHasMultiTypes) {
      const aDiff = Math.abs(a.length - 2);
      const bDiff = Math.abs(b.length - 2);
      return aDiff - bDiff;
    }

    return 0;
  });

  return sortedCombinations;
}

// Find rate plans common to all rooms in a combination
function findCommonRatePlans(combination) {
  if (!combination || combination.length === 0) return [];

  // Get rate plans from first room type
  let commonRatePlans = Object.keys(combination[0].room.ratePlans || {});

  // Filter to only those available in all room types
  for (let i = 1; i < combination.length; i++) {
    const roomRatePlans = Object.keys(combination[i].room.ratePlans || {});
    commonRatePlans = commonRatePlans.filter((rp) =>
      roomRatePlans.includes(rp)
    );
  }

  return commonRatePlans;
}

// Calculate package prices for all rate plans
function calculatePackageForAllRatePlans(
  combination,
  totalAdults,
  totalChildren,
  totalInfants,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  // SIMPLIFIED: Just use whatever rate plan is available for each room
  // Don't worry about common rate plans

  // For multi-room combinations, just use the first/best rate plan for each room
  if (combination.length > 1) {
    // Calculate best package using independent rate plans for each room type
    let packageRooms = [];
    let totalPrice = 0;
    let remainingAdults = totalAdults;
    let remainingChildren = totalChildren;
    let remainingInfants = totalInfants;

    // Process each room type - SIMPLIFIED: just use first available rate plan
    for (const roomTypeInfo of combination) {
      const { room, count } = roomTypeInfo;

      // Just get the FIRST available rate plan - don't overcomplicate
      const ratePlans = Object.entries(room.ratePlans || {});
      if (ratePlans.length === 0) {
        return null;
      }

      // For budget package, find the CHEAPEST rate plan
      let cheapestRatePlan = null;
      let cheapestRatePlanCode = null;
      let cheapestPrice = Infinity;

      for (const [code, rp] of ratePlans) {
        if (!rp) continue;
        // Get the base price from the rate plan
        let price = Infinity;
        if (rp.pricing && rp.pricing[0]) {
          if (rp.pricing[0].priceForPax && rp.pricing[0].priceForPax[0]) {
            price =
              rp.pricing[0].priceForPax[0].priceBeforeTax ||
              rp.pricing[0].priceForPax[0].price ||
              Infinity;
          } else if (rp.pricing[0].price) {
            price = rp.pricing[0].price;
          }
        }

        if (price < cheapestPrice) {
          cheapestPrice = price;
          const actualCode = rp.ratePlanCode || code;
          cheapestRatePlan = {
            ...rp,
            code: actualCode,
            ratePlanCode: actualCode,
          }; // Add both code and ratePlanCode
          cheapestRatePlanCode = actualCode;
        }
      }

      if (!cheapestRatePlan) {
        return null;
      }

      const ratePlanCode = cheapestRatePlanCode;
      const ratePlan = cheapestRatePlan; // Now includes code property

      // Create limited room for this count
      const limitedRoom = {
        ...room,
        availableRooms: count,
      };

      // Find optimal distribution for this room/ratePlan
      const optimal = findOptimalDistributionForRoom(
        limitedRoom,
        ratePlan,
        remainingAdults,
        remainingChildren,
        remainingInfants,
        "budget", // Use budget optimization
        promotions,
        taxes,
        hotelData,
        dateCount
      );

      if (!optimal) {
      } else {
      }

      const bestRoomsForThisType = optimal ? optimal.roomDetails : null;

      // Use the rate plan found for this room type
      if (bestRoomsForThisType) {
        for (const roomDetail of bestRoomsForThisType) {
          packageRooms.push(roomDetail);
          totalPrice += roomDetail.totalPrice;
        }

        // Update remaining guests based on best option
        for (const roomDetail of bestRoomsForThisType) {
          remainingAdults -= roomDetail.adults;
          remainingChildren -= roomDetail.children;
          remainingInfants -= roomDetail.infants;
        }
      } else {
        // Could not find valid rate plan for this room type
        return null;
      }
    }

    // Check if all guests were accommodated
    if (
      remainingAdults <= 0 &&
      remainingChildren <= 0 &&
      remainingInfants <= 0
    ) {
      // Return the multi-room package - SIMPLIFIED
      const totalDiscount = packageRooms.reduce(
        (sum, r) => sum + (r.discount || 0),
        0
      );

      // Return in a format that findOptimalMultiRoomPackage expects
      return {
        standard: {
          // Use 'standard' instead of 'mixed' for simplicity
          name: "Standard",
          code: "standard",
          rooms: packageRooms,
          totalPrice: totalPrice,
          totalDiscount: totalDiscount,
        },
      };
    } else {
      // Could not accommodate all guests

      return null;
    }
  }

  // For single room type, just use first available rate plan
  if (combination.length === 1) {
    const room = combination[0].room;
    const count = combination[0].count;

    const ratePlans = Object.entries(room.ratePlans || {});
    if (ratePlans.length === 0) {
      return null;
    }

    // For budget package (single room), find the CHEAPEST rate plan
    let cheapestRatePlan = null;
    let cheapestRatePlanCode = null;
    let cheapestPrice = Infinity;

    for (const [code, rp] of ratePlans) {
      if (!rp) continue;
      // Get the base price from the rate plan
      let price = Infinity;
      if (rp.pricing && rp.pricing[0]) {
        if (rp.pricing[0].priceForPax && rp.pricing[0].priceForPax[0]) {
          price =
            rp.pricing[0].priceForPax[0].priceBeforeTax ||
            rp.pricing[0].priceForPax[0].price ||
            Infinity;
        } else if (rp.pricing[0].price) {
          price = rp.pricing[0].price;
        }
      }

      if (price < cheapestPrice) {
        cheapestPrice = price;
        const actualCode = rp.ratePlanCode || code;
        cheapestRatePlan = {
          ...rp,
          code: actualCode,
          ratePlanCode: actualCode,
        }; // Add both code and ratePlanCode
        cheapestRatePlanCode = actualCode;
      }
    }

    if (!cheapestRatePlan) {
      return null;
    }

    const ratePlanCode = cheapestRatePlanCode;
    const ratePlan = cheapestRatePlan; // Now includes code property

    // Create limited room for this count
    const limitedRoom = {
      ...room,
      availableRooms: count,
    };

    // Find optimal distribution
    const optimal = findOptimalDistributionForRoom(
      limitedRoom,
      ratePlan,
      totalAdults,
      totalChildren,
      totalInfants,
      "budget",
      promotions,
      taxes,
      hotelData,
      dateCount
    );

    if (!optimal || !optimal.roomDetails) {
      return null;
    }

    return {
      [ratePlanCode]: {
        name: ratePlan.name || ratePlanCode,
        code: ratePlanCode,
        rooms: optimal.roomDetails,
        totalPrice: optimal.totalPrice,
        totalDiscount: optimal.roomDetails.reduce(
          (sum, r) => sum + (r.discount || 0),
          0
        ),
      },
    };
  }

  // This shouldn't be reached anymore but returning empty
  return {};

  // Legacy code below - not used anymore
  /*
  const ratePlanOptions = {};
  let validOptions = 0;
  const commonRatePlans = []; // Fix ESLint error
  
  // Calculate price for each common rate plan
  for (const ratePlanCode of commonRatePlans) {
    let packageRooms = [];
    let totalPrice = 0;
    let remainingAdults = totalAdults;
    let remainingChildren = totalChildren;
    let remainingInfants = totalInfants;
    let isValid = true;
    
    // Process each room type with this rate plan
    for (const roomTypeInfo of combination) {
      const { room, count } = roomTypeInfo;
      const ratePlan = room.ratePlans[ratePlanCode];
      
      if (!ratePlan) {
        isValid = false;
        break;
      }
      
      // Add code to ratePlan for consistency
      const ratePlanWithCode = { ...ratePlan, code: ratePlanCode };
      
      // Create limited room for this count
      const limitedRoom = {
        ...room,
        availableRooms: count
      };
      
      // Find optimal distribution for this room/ratePlan
      const optimal = findOptimalDistributionForRoom(
        limitedRoom,
        ratePlanWithCode,
        remainingAdults,
        remainingChildren,
        remainingInfants,
        'budget', // Use budget optimization for all
        promotions,
        taxes,
        hotelData,
        dateCount
      );
      
      if (optimal && optimal.roomDetails) {
        for (const roomDetail of optimal.roomDetails) {
          packageRooms.push(roomDetail);
          totalPrice += roomDetail.totalPrice;
        }
        
        // Update remaining guests
        for (const occupancy of optimal.distribution) {
          remainingAdults -= occupancy.adults;
          remainingChildren -= occupancy.children;
          remainingInfants -= occupancy.infants;
        }
      } else {
        isValid = false;
        break;
      }
    }
    
    // If this rate plan works for all rooms
    if (isValid && remainingAdults <= 0 && remainingChildren <= 0 && remainingInfants <= 0) {
      // Get rate plan name from any room that has it
      let ratePlanName = ratePlanCode;
      for (const roomTypeInfo of combination) {
        if (roomTypeInfo.room.ratePlans[ratePlanCode]?.name) {
          ratePlanName = roomTypeInfo.room.ratePlans[ratePlanCode].name;
          break;
        }
      }
      
      ratePlanOptions[ratePlanCode] = {
        name: ratePlanName,
        code: ratePlanCode,
        rooms: packageRooms,
        totalPrice: totalPrice,
        totalDiscount: packageRooms.reduce((sum, r) => sum + (r.discount || 0), 0)
      };
      validOptions++;
    }
  }
  
  if (validOptions === 0) {
    return null;
  }
  
  return ratePlanOptions;
  */
}

// Find optimal package across multiple room types
function findOptimalMultiRoomPackage(
  rooms,
  totalAdults,
  totalChildren,
  totalInfants,
  optimizationType,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  if (!PACKAGE_CONFIG.ENABLE_MULTI_ROOM_TYPES) {
    return null;
  }

  // Generate all possible room type combinations
  const roomCombinations = generateRoomTypeCombinations(
    rooms,
    totalAdults,
    totalChildren,
    totalInfants,
    PACKAGE_CONFIG.MAX_ROOM_TYPE_MIX
  );

  if (roomCombinations.length === 0) {
    return null;
  }

  let bestPackage = null;
  let bestPrice = optimizationType === "budget" ? Infinity : 0;

  // For smart-mix, filter to only multi-room type combinations (different room types)
  let combinationsToEvaluate = roomCombinations;

  // For budget, also prioritize multi-room combinations when there's limited availability
  if (optimizationType === "budget") {
    // Check if we have multi-room combinations that might be better for limited availability
    const multiRoomCombos = roomCombinations.filter(
      (combo) => combo.length > 1
    );

    if (multiRoomCombos.length > 0) {
      // Evaluate both single and multi-room, but log them separately
    }
  }

  if (optimizationType === "smart-mix") {
    combinationsToEvaluate = roomCombinations.filter(
      (combo) => combo.length > 1
    );

    if (combinationsToEvaluate.length === 0) {
      return null;
    }
  }

  // Evaluate each room combination
  for (const combination of combinationsToEvaluate) {
    // Calculate prices for all rate plans for this combination
    const ratePlanOptions = calculatePackageForAllRatePlans(
      combination,
      totalAdults,
      totalChildren,
      totalInfants,
      promotions,
      taxes,
      hotelData,
      dateCount
    );

    if (!ratePlanOptions || Object.keys(ratePlanOptions).length === 0) {
      continue;
    }

    // Select the best rate plan based on optimization type
    let selectedRatePlan = null;
    let selectedPrice = optimizationType === "budget" ? Infinity : 0;

    for (const [code, option] of Object.entries(ratePlanOptions)) {
      const shouldSelect =
        optimizationType === "budget"
          ? option.totalPrice < selectedPrice
          : option.totalPrice > selectedPrice;

      if (shouldSelect) {
        selectedRatePlan = code;
        selectedPrice = option.totalPrice;
      }
    }

    if (!selectedRatePlan) continue;

    const selectedOption = ratePlanOptions[selectedRatePlan];

    // Check if this is the best combination so far
    const shouldUpdateBest =
      optimizationType === "budget"
        ? selectedPrice < bestPrice
        : optimizationType === "luxury"
        ? selectedPrice > bestPrice
        : true;

    if (shouldUpdateBest) {
      bestPackage = {
        rooms: selectedOption.rooms,
        totalPrice: selectedOption.totalPrice,
        totalDiscount: selectedOption.totalDiscount,
        roomTypes: selectedOption.rooms.map((r) => r.roomName || "Room"),
        isMultiRoom: combination.length > 1,
        ratePlanOptions: ratePlanOptions,
        selectedRatePlan: selectedRatePlan,
        combination: combination.map((c) => {
          const roomName =
            c.room.name ||
            c.room.description?.substring(0, 30) ||
            `Room ${c.room.id}`;
          return { roomName, count: c.count };
        }),
      };
      bestPrice = selectedPrice;
    }
  }

  if (bestPackage) {
  } else {
  }

  return bestPackage;
}

// Calculate price for a single room with given guest distribution
function calculateRoomPrice(
  room,
  adults,
  children,
  infants,
  ratePlan,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  try {
    if (!room || !ratePlan) return null;

    const minorObj =
      room.pricingAdditionalGuests?.[ratePlan.code] ||
      room.pricingAdditionalGuests?.["standard"] ||
      {};
    let pricing = [];

    // Handle different pricing structures
    if (ratePlan.code && room.pricing && Array.isArray(room.pricing)) {
      pricing = room.pricing.filter(
        (p) => p && p.ratePlanCode === ratePlan.code
      );
    }

    // If no pricing with ratePlanCode, use direct pricing
    if (pricing.length === 0 && room.pricing && Array.isArray(room.pricing)) {
      pricing = room.pricing;
    }

    // Find pricing for this guest combination
    let adultPricing = pricing.find((p) => p && p.adultCount === adults);

    // If exact match not found, try to find closest or use default
    if (!adultPricing && pricing.length > 0) {
      // Try to find pricing that can accommodate the adults
      adultPricing = pricing.find(
        (p) => p && (!p.adultCount || p.adultCount >= adults)
      );

      // If still not found, use first available pricing
      if (!adultPricing) {
        adultPricing = pricing[0];
      }
    }

    // Check if we have valid pricing data
    if (!adultPricing) {
      const roomName =
        room.name || room.description?.substring(0, 30) || `Room ${room.id}`;

      return null;
    }

    // Handle different pricing structures
    let priceData = adultPricing.priceForPax || [];

    // If we have priceForPax but wrong number of dates, adjust it
    if (priceData.length > 0 && priceData.length !== dateCount) {
      if (priceData.length > dateCount) {
        // Truncate to match date count
        priceData = priceData.slice(0, dateCount);
      } else {
        // Repeat last price for remaining dates
        const lastPrice = priceData[priceData.length - 1];
        while (priceData.length < dateCount) {
          priceData.push(lastPrice);
        }
      }
    }

    // If no priceForPax, create from single price
    if ((!priceData || priceData.length === 0) && adultPricing.price) {
      // Create price data from single price
      priceData = Array(dateCount)
        .fill(null)
        .map((_, index) => ({
          checkInDate: new Date(),
          priceBeforeTax: adultPricing.price || 0,
          price: adultPricing.price || 0,
        }));
    }

    if (!priceData || priceData.length === 0) {
      return null;
    }

    const roomName =
      room.name || room.description?.substring(0, 30) || `Room ${room.id}`;

    let totalPrice = 0;
    let totalTax = 0;
    let discount = 0;
    let basePriceSum = 0;

    // Arrange promotions properly
    const arrangedPromos = arrangePromos(promotions || []);

    // Calculate base price for each date
    priceData.forEach((datePrice, idx) => {
      let price = datePrice.priceBeforeTax || datePrice.price || 0;
      basePriceSum += price;

      // Add child pricing
      if (
        children > 0 &&
        minorObj?.children &&
        Array.isArray(minorObj.children)
      ) {
        const childPrice = minorObj.children.find(
          (c) => c && c.checkInDate === datePrice.checkInDate
        );
        if (childPrice && childPrice.priceBeforeTax) {
          price += (childPrice.priceBeforeTax || 0) * children;
        }
      }

      // Add infant pricing
      if (infants > 0 && minorObj?.infants && Array.isArray(minorObj.infants)) {
        const infantPrice = minorObj.infants.find(
          (i) => i && i.checkInDate === datePrice.checkInDate
        );
        if (infantPrice && infantPrice.priceBeforeTax) {
          price += (infantPrice.priceBeforeTax || 0) * infants;
        }
      }

      // Apply promotions
      const promo = getApplicablePromotionRoom({
        arrangedPromos: arrangedPromos,
        rp: { rpObject: ratePlan, roomId: room.id },
        tempPrice: price,
        checkInDate: datePrice.checkInDate,
      });

      if (promo) {
        const { finalValue, reducedPrice } = calculatePromotion({
          price,
          promo,
          idx,
          dateCount,
        });
        price = finalValue;
        discount += reducedPrice;
      }

      // Calculate tax
      const taxObj = getCalculatedTax({
        rooms: [{ id: room.id, amount: price }],
        isInclusive: hotelData?.isTaxIncluded,
        taxes: taxes,
        noOfNights: 1,
      });

      totalPrice += price;
      totalTax += taxObj.totalRoomTax;
    });

    const finalPrice = totalPrice + totalTax;

    // Use roomName property (from API) first, then name, then fallback
    const finalRoomName = room.roomName || room.name || `Room ${room.id}`;

    // Find the idx of this rate plan in the room's sorted rate plans
    // MUST match exactly how Room.js sorts them
    let ratePlanIdx = 0;
    if (room.ratePlans) {
      // Get all rate plans as array with their codes
      const ratePlanEntries = Object.entries(room.ratePlans).map(
        ([key, rp]) => ({
          ...rp,
          ratePlanCode: rp.ratePlanCode || key, // Ensure ratePlanCode is set
          _key: key, // Keep original key for reference
        })
      );

      // Sort the same way Room.js does
      const sortedRatePlans = ratePlanEntries.sort((a, b) => {
        // If prices.priceAfterDiscount exists, use it (like Room.js)
        if (
          a?.prices?.priceAfterDiscount !== undefined ||
          b?.prices?.priceAfterDiscount !== undefined
        ) {
          const priceA = a?.prices?.priceAfterDiscount || Number.MAX_VALUE;
          const priceB = b?.prices?.priceAfterDiscount || Number.MAX_VALUE;
          return priceA - priceB;
        }

        // Otherwise fall back to pricing data
        let priceA = Number.MAX_VALUE;
        let priceB = Number.MAX_VALUE;

        if (a?.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax) {
          priceA = a.pricing[0].priceForPax[0].priceBeforeTax;
        } else if (a?.pricing?.[0]?.price) {
          priceA = a.pricing[0].price;
        }

        if (b?.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax) {
          priceB = b.pricing[0].priceForPax[0].priceBeforeTax;
        } else if (b?.pricing?.[0]?.price) {
          priceB = b.pricing[0].price;
        }

        return priceA - priceB;
      });

      // Find the index matching our rate plan
      // The ratePlan.code should now be the actual ratePlanCode
      ratePlanIdx = sortedRatePlans.findIndex((rp) => {
        const rpCode = rp.ratePlanCode || rp._key;
        // Check all possible matches
        return (
          rpCode === ratePlan.ratePlanCode ||
          rpCode === ratePlan.code ||
          rp._key === ratePlan.ratePlanCode ||
          rp._key === ratePlan.code
        );
      });

      if (ratePlanIdx === -1) {
        ratePlanIdx = 0; // Fallback to 0 if not found
      }
    }

    return {
      roomId: room.id,
      roomName: finalRoomName,
      roomDescription: room.description,
      roomImages: room.RoomImages,
      roomAmenities: room.HotelRoomAmenities,
      maxGuestCount: room.maxGuestCount,
      defaultGuestCount: room.defaultGuestCount,
      ratePlanId: ratePlan.id,
      ratePlanCode: ratePlan.ratePlanCode || ratePlan.code, // Use ratePlanCode if available
      ratePlanName: ratePlan.name || "Standard",
      ratePlanIdx: ratePlanIdx, // Add the idx for proper selection
      adults,
      children,
      infants,
      basePrice: totalPrice,
      tax: totalTax,
      discount,
      totalPrice: finalPrice,
      ratePlan,
    };
  } catch (error) {
    console.error("Error calculating room price:", error);
    return null;
  }
}

// Generate budget package (cheapest rooms)
function generateBudgetPackage(
  rooms,
  totalAdult,
  totalChild,
  totalInfant,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  try {
    // First try multi-room optimization for potentially better prices
    if (PACKAGE_CONFIG.ENABLE_MULTI_ROOM_TYPES) {
      const multiRoomPackage = findOptimalMultiRoomPackage(
        rooms,
        totalAdult,
        totalChild,
        totalInfant,
        "budget",
        promotions,
        taxes,
        hotelData,
        dateCount
      );

      if (multiRoomPackage) {
        return {
          type: "budget",
          name: multiRoomPackage.isMultiRoom
            ? "Smart Budget Mix"
            : "Budget Package",
          description: multiRoomPackage.isMultiRoom
            ? `Optimal mix: ${multiRoomPackage.roomTypes.join(" + ")}`
            : "Most affordable room combination",
          rooms: multiRoomPackage.rooms,
          totalPrice: multiRoomPackage.totalPrice,
          totalDiscount: multiRoomPackage.totalDiscount,
          roomCount: multiRoomPackage.rooms.length,
          color: "#10b981", // Green
          icon: "wallet",
          isMultiRoom: multiRoomPackage.isMultiRoom,
          ratePlanOptions: multiRoomPackage.ratePlanOptions,
          selectedRatePlan: multiRoomPackage.selectedRatePlan,
        };
      }
    }

    // Fall back to optimal distribution approach

    // Sort rooms by lowest price first (ensure cheapest rooms are used first)
    const sortedRooms = [...rooms].sort((a, b) => {
      // Get the cheapest price from any rate plan for comparison
      let aPrice = Infinity;
      let bPrice = Infinity;

      // Check rate plan prices
      if (a.ratePlans) {
        Object.values(a.ratePlans).forEach((rp) => {
          if (rp?.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax) {
            aPrice = Math.min(
              aPrice,
              rp.pricing[0].priceForPax[0].priceBeforeTax
            );
          } else if (rp?.pricing?.[0]?.price) {
            aPrice = Math.min(aPrice, rp.pricing[0].price);
          }
        });
      }

      // Fallback to old pricing structure
      if (aPrice === Infinity) {
        aPrice =
          a.pricing?.[0]?.price ||
          a.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax ||
          Infinity;
      }

      // Same for room b
      if (b.ratePlans) {
        Object.values(b.ratePlans).forEach((rp) => {
          if (rp?.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax) {
            bPrice = Math.min(
              bPrice,
              rp.pricing[0].priceForPax[0].priceBeforeTax
            );
          } else if (rp?.pricing?.[0]?.price) {
            bPrice = Math.min(bPrice, rp.pricing[0].price);
          }
        });
      }

      if (bPrice === Infinity) {
        bPrice =
          b.pricing?.[0]?.price ||
          b.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax ||
          Infinity;
      }

      return aPrice - bPrice;
    });

    sortedRooms.forEach((room, idx) => {
      const roomName =
        room.name || room.description?.substring(0, 30) || `Room ${room.id}`;
    });

    // Use the optimized distribution function that uses ALL available cheapest rooms first
    const packageRooms = distributeGuestsOptimally(
      sortedRooms,
      totalAdult,
      totalChild,
      totalInfant,
      promotions,
      taxes,
      hotelData,
      dateCount
    );

    if (!packageRooms) {
      return null;
    }

    const totalPrice = packageRooms.reduce(
      (sum, room) => sum + room.totalPrice,
      0
    );
    const totalDiscount = packageRooms.reduce(
      (sum, room) => sum + room.discount,
      0
    );

    // Show room type breakdown
    const roomTypeBreakdown = {};
    packageRooms.forEach((room) => {
      const roomType = room.roomName || "Unknown";
      if (!roomTypeBreakdown[roomType]) {
        roomTypeBreakdown[roomType] = { count: 0, totalPrice: 0 };
      }
      roomTypeBreakdown[roomType].count++;
      roomTypeBreakdown[roomType].totalPrice += room.totalPrice;
    });

    // For single room type fallback, create ratePlanOptions from the selected rooms
    // Since we selected the best rate plan already, we only have one option
    const ratePlanCode = packageRooms[0]?.ratePlanCode || "standard";
    const ratePlanOptions = {
      [ratePlanCode]: {
        name: packageRooms[0]?.ratePlanName || ratePlanCode,
        code: ratePlanCode,
        rooms: packageRooms,
        totalPrice: totalPrice,
        totalDiscount: totalDiscount,
      },
    };

    return {
      type: "budget",
      name: "Budget Package",
      description: "Most affordable room combination",
      rooms: packageRooms,
      totalPrice,
      totalDiscount,
      roomCount: packageRooms.length,
      color: "#10b981", // Green
      icon: "wallet",
      ratePlanOptions: ratePlanOptions,
      selectedRatePlan: ratePlanCode,
    };
  } catch (error) {
    console.error("Error generating budget package:", error);
    return null;
  }
}

// Generate convenience package (same room type)
function generateConveniencePackage(
  rooms,
  totalAdult,
  totalChild,
  totalInfant,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  try {
    const packageOptions = [];

    for (const room of rooms) {
      if (!room || !room.availableRooms || room.availableRooms === 0) continue;

      // Handle rooms with ratePlans
      if (room.ratePlans && Object.keys(room.ratePlans).length > 0) {
        for (const [code, ratePlan] of Object.entries(room.ratePlans || {})) {
          if (!ratePlan) continue;

          // Find optimal distribution for convenience (balanced occupancy)
          const optimal = findOptimalDistributionForRoom(
            room,
            {
              ...ratePlan,
              code: ratePlan.ratePlanCode || code,
              ratePlanCode: ratePlan.ratePlanCode || code,
            }, // Add proper code to ratePlan
            totalAdult,
            totalChild,
            totalInfant,
            "convenience", // Optimization type - prefers balanced occupancy
            promotions,
            taxes,
            hotelData,
            dateCount
          );

          if (optimal) {
            const roomName =
              room.name ||
              room.description?.substring(0, 30) ||
              `Room ${room.id}`;

            packageOptions.push({
              rooms: optimal.roomDetails,
              totalPrice: optimal.totalPrice,
              totalDiscount: optimal.roomDetails.reduce(
                (sum, r) => sum + r.discount,
                0
              ),
              roomType:
                room.name ||
                room.description?.substring(0, 30) ||
                `Room ${room.id}`,
              distribution: optimal.distribution,
            });
          }
        }
      }
    }

    // Select the best convenience option (middle price range)

    if (packageOptions.length === 0) return null;
    // console.log({ packageOptions });

    packageOptions.sort((a, b) => a.totalPrice - b.totalPrice);
    const selectedOption =
      packageOptions.length <= 2
        ? packageOptions[0]
        : packageOptions[Math.floor(packageOptions.length / 2)];

    // For single room type, create ratePlanOptions from the selected rooms
    const ratePlanCode = selectedOption.rooms[0]?.ratePlanCode || "standard";
    const ratePlanOptions = {
      [ratePlanCode]: {
        name: selectedOption.rooms[0]?.ratePlanName || ratePlanCode,
        code: ratePlanCode,
        rooms: selectedOption.rooms,
        totalPrice: selectedOption.totalPrice,
        totalDiscount: selectedOption.totalDiscount,
      },
    };

    return {
      type: "convenience",
      name: "Convenience Package",
      description: `All ${selectedOption.roomType} rooms`,
      rooms: selectedOption.rooms,
      totalPrice: selectedOption.totalPrice,
      totalDiscount: selectedOption.totalDiscount,
      roomCount: selectedOption.rooms.length,
      color: "#3b82f6", // Blue
      icon: "sparkles",
      ratePlanOptions: ratePlanOptions,
      selectedRatePlan: ratePlanCode,
    };
  } catch (error) {
    console.error("Error generating convenience package:", error);
    return null;
  }
}

// Generate smart mix package (balanced multi-room combination)
function generateSmartMixPackage(
  rooms,
  totalAdult,
  totalChild,
  totalInfant,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  try {
    if (!PACKAGE_CONFIG.ENABLE_MULTI_ROOM_TYPES) {
      return null; // Smart mix requires multi-room support
    }

    // Find optimal multi-room combination with balanced criteria
    const multiRoomPackage = findOptimalMultiRoomPackage(
      rooms,
      totalAdult,
      totalChild,
      totalInfant,
      "smart-mix",
      promotions,
      taxes,
      hotelData,
      dateCount
    );

    if (!multiRoomPackage) {
      return null;
    }

    // Additional filtering for smart mix - prefer combinations with 2-3 room types
    const roomTypeCount = new Set(multiRoomPackage.roomTypes).size;

    // Create a descriptive name based on room mix
    const roomTypeCounts = {};
    multiRoomPackage.roomTypes.forEach((type) => {
      roomTypeCounts[type] = (roomTypeCounts[type] || 0) + 1;
    });

    const description = Object.entries(roomTypeCounts)
      .map(([type, count]) => `${count}x ${type}`)
      .join(" + ");

    return {
      type: "smart-mix",
      name: "Smart Mix Package",
      description: description,
      rooms: multiRoomPackage.rooms,
      totalPrice: multiRoomPackage.totalPrice,
      totalDiscount: multiRoomPackage.totalDiscount,
      roomCount: multiRoomPackage.rooms.length,
      color: "#8b5cf6", // Purple
      icon: "sparkles",
      isMultiRoom: true,
      roomTypeCount: roomTypeCount,
      ratePlanOptions: multiRoomPackage.ratePlanOptions,
      selectedRatePlan: multiRoomPackage.selectedRatePlan,
    };
  } catch (error) {
    console.error("Error generating smart mix package:", error);
    return null;
  }
}

// Generate luxury package (most expensive rooms)
function generateLuxuryPackage(
  rooms,
  totalAdult,
  totalChild,
  totalInfant,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  try {
    // Sort rooms by highest price first
    const sortedRooms = [...rooms].reverse();

    const packageRooms = [];
    let remainingAdults = totalAdult;
    let remainingChildren = totalChild;
    let remainingInfants = totalInfant;

    console.log({ sortedRooms });

    for (const room of sortedRooms) {
      if (
        !room ||
        (remainingAdults === 0 &&
          remainingChildren === 0 &&
          remainingInfants === 0)
      )
        break;

      // Check if room has available inventory

      const roomAvailability = room.availableRooms || 0;
      if (roomAvailability === 0) continue;

      // Only process rooms with ratePlans
      if (room.ratePlans && Object.keys(room.ratePlans).length > 0) {
        // Check each rate plan, prefer higher priced ones
        const ratePlanEntries = Object.entries(room.ratePlans || {}).sort(
          (a, b) => {
            // Get price from rate plan a
            let aPrice = 0;
            const ratePlanA = a[1];
            if (ratePlanA?.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax) {
              aPrice = ratePlanA.pricing[0].priceForPax[0].priceBeforeTax;
            } else if (ratePlanA?.pricing?.[0]?.price) {
              aPrice = ratePlanA.pricing[0].price;
            }

            // Get price from rate plan b
            let bPrice = 0;
            const ratePlanB = b[1];
            if (ratePlanB?.pricing?.[0]?.priceForPax?.[0]?.priceBeforeTax) {
              bPrice = ratePlanB.pricing[0].priceForPax[0].priceBeforeTax;
            } else if (ratePlanB?.pricing?.[0]?.price) {
              bPrice = ratePlanB.pricing[0].price;
            }

            return bPrice - aPrice; // Descending order (highest price first)
          }
        );

        // Try each rate plan to find the best (most expensive) option
        let bestOption = null;
        let bestPrice = 0;

        for (const [code, ratePlan] of ratePlanEntries) {
          if (!ratePlan) continue;

          // Find optimal distribution for luxury (most expensive)
          const optimal = findOptimalDistributionForRoom(
            room,
            { ...ratePlan, code }, // Add code to ratePlan
            remainingAdults,
            remainingChildren,
            remainingInfants,
            "luxury", // Optimization type - selects most expensive
            promotions,
            taxes,
            hotelData,
            dateCount
          );

          if (optimal && optimal.totalPrice > bestPrice) {
            bestOption = optimal;
            bestPrice = optimal.totalPrice;
          }
        }

        // Use the best (most expensive) option found
        if (bestOption) {
          const roomName =
            room.name ||
            room.description?.substring(0, 30) ||
            `Room ${room.id}`;

          // Add all rooms from the optimal distribution
          for (const roomDetail of bestOption.roomDetails) {
            packageRooms.push(roomDetail);
          }

          // Update remaining guests
          for (const occupancy of bestOption.distribution) {
            remainingAdults -= occupancy.adults;
            remainingChildren -= occupancy.children;
            remainingInfants -= occupancy.infants;
          }

          // If all guests accommodated, we can stop
          if (
            remainingAdults === 0 &&
            remainingChildren === 0 &&
            remainingInfants === 0
          ) {
            break;
          }
        }
      }
    }

    if (remainingAdults > 0 || remainingChildren > 0 || remainingInfants > 0) {
      return null;
    }

    const totalPrice = packageRooms.reduce(
      (sum, room) => sum + room.totalPrice,
      0
    );
    const totalDiscount = packageRooms.reduce(
      (sum, room) => sum + room.discount,
      0
    );

    // For single room type fallback, create ratePlanOptions from the selected rooms
    const ratePlanCode = packageRooms[0]?.ratePlanCode || "standard";
    const ratePlanOptions = {
      [ratePlanCode]: {
        name: packageRooms[0]?.ratePlanName || ratePlanCode,
        code: ratePlanCode,
        rooms: packageRooms,
        totalPrice: totalPrice,
        totalDiscount: totalDiscount,
      },
    };

    return {
      type: "luxury",
      name: "Luxury Package",
      description: "Premium room selection",
      rooms: packageRooms,
      totalPrice,
      totalDiscount,
      roomCount: packageRooms.length,
      color: "#f59e0b", // Gold/Amber
      icon: "crown",
      ratePlanOptions: ratePlanOptions,
      selectedRatePlan: ratePlanCode,
    };
  } catch (error) {
    console.error("Error generating luxury package:", error);
    return null;
  }
}

// Main function to generate all packages
export function generatePackages(
  rooms,
  totalAdult,
  totalChild,
  totalInfant,
  promotions,
  taxes,
  hotelData,
  dateCount
) {
  try {
    if (!rooms || rooms.length === 0) {
      return [];
    }
    if (!totalAdult || totalAdult === 0) {
      return [];
    }
    if (!dateCount || dateCount === 0) {
      return [];
    }

    // Ensure numbers are valid
    totalAdult = parseInt(totalAdult) || 0;
    totalChild = parseInt(totalChild) || 0;
    totalInfant = parseInt(totalInfant) || 0;
    dateCount = parseInt(dateCount) || 1;

    if (totalAdult === 0) return [];

    // Early exit: Don't generate packages for small groups
    if (totalAdult < PACKAGE_CONFIG.MINIMUM_ADULTS_FOR_PACKAGES) {
      if (PACKAGE_CONFIG.ENABLE_PACKAGE_LOGGING) {
      }
      return [];
    }

    // Early exit: Check if any single room can accommodate all guests
    const totalGuests = totalAdult + totalChild;
    const canFitInSingleRoom = rooms.some((room) => {
      const maxCapacity = room.maxGuestCount || 4;
      const hasAvailability =
        room.availableRooms > 0 ||
        (room.ratePlans &&
          Object.values(room.ratePlans).some(
            (rp) => rp && rp.availableRooms > 0
          ));
      return hasAvailability && maxCapacity >= totalGuests;
    });

    // If a single room can fit all guests and we have less than 5 adults, skip packages
    if (canFitInSingleRoom && totalAdult < 5) {
      if (PACKAGE_CONFIG.ENABLE_PACKAGE_LOGGING) {
      }
      return [];
    }

    // Filter out invalid rooms
    const validRooms = rooms.filter((room) => {
      return (
        room && (room.pricing || room.ratePlans) && room.availableRooms > 0
      );
    });

    if (validRooms.length === 0) {
      return [];
    }

    const packages = [];

    // Generate budget package
    try {
      const budgetPackage = generateBudgetPackage(
        validRooms,
        totalAdult,
        totalChild,
        totalInfant,
        promotions || [],
        taxes || [],
        hotelData || {},
        dateCount
      );
      if (budgetPackage) packages.push(budgetPackage);
    } catch (error) {
      console.error("Error in budget package generation:", error);
    }

    // Generate smart mix package (if multi-room is enabled)
    if (PACKAGE_CONFIG.ENABLE_MULTI_ROOM_TYPES) {
      try {
        const smartMixPackage = generateSmartMixPackage(
          validRooms,
          totalAdult,
          totalChild,
          totalInfant,
          promotions || [],
          taxes || [],
          hotelData || {},
          dateCount
        );
        if (smartMixPackage) packages.push(smartMixPackage);
      } catch (error) {
        console.error("Error in smart mix package generation:", error);
      }
    }

    // Generate convenience package
    try {
      const conveniencePackage = generateConveniencePackage(
        validRooms,
        totalAdult,
        totalChild,
        totalInfant,
        promotions || [],
        taxes || [],
        hotelData || {},
        dateCount
      );

      if (conveniencePackage) packages.push(conveniencePackage);
    } catch (error) {
      console.error("Error in convenience package generation:", error);
    }

    // Generate luxury package
    try {
      const luxuryPackage = generateLuxuryPackage(
        validRooms,
        totalAdult,
        totalChild,
        totalInfant,
        promotions || [],
        taxes || [],
        hotelData || {},
        dateCount
      );

      if (luxuryPackage) packages.push(luxuryPackage);
    } catch (error) {
      console.error("Error in luxury package generation:", error);
    }

    return packages;
  } catch (error) {
    console.error("Fatal error in generatePackages:", error);
    return [];
  }
}

// Export configuration for external use/override
export { PACKAGE_CONFIG };
